<template>
  <MultiStepDialog
    :steps="steps"
    :size="maxSmall ? 'full' : undefined"
    :open="open"
    :showFooter="dialogRef?.currentStep !== 'buildings'"
    :dismissible="!form.isDirty"
    :disableConfirm="!componentRef?.isDirty"
    :closeOnCancel="false"
    :withClose="dialogRef?.currentStep === 'buildings'"
    :showConfirm="dialogRef?.currentStep !== 'buildings'"
    :showCancel="dialogRef?.currentStep !== 'buildings'"
    :closeOnConfirm="false"
    @update:open="handleUpdateOpen"
    @cancel="dialogRef?.handleStepBack()"
    @confirm="componentRef?.submit()"
    ref="dialogRef"
    background="white"
    variant="create"
    trackingId="assets.new"
  >
    <slot :disabled="!canCreateAssets" />
    <template v-if="dialogRef?.currentStep === 'overview'" #header>
      <Header
        :buildingId="buildingId"
        :buildings="buildings"
        :form="form"
        @update:open="handleUpdateOpen"
        @update:form="handleUpdateForm"
        @goToStep="dialogRef?.handleGoToStep($event)"
      />
    </template>
    <template #content="{ step, props, handleGoToStep }">
      <Component
        ref="componentRef"
        :is="step.content"
        v-bind="props"
        :query="dialogRef?.query"
        class="grow overflow-y-auto min-h-[350px]"
        @goToStep="handleGoToStep"
        @submit="handleSubmit"
        @update:form="handleUpdateForm"
        @clear:query="dialogRef?.clearQuery()"
      />
    </template>
    <template v-if="dialogRef?.currentStep === 'overview'" #footer>
      <Footer
        :form="form"
        :createMore="createMore"
        :isLoading="isLoading"
        @update:create-more="handleUpdateCreateMore"
        @submit="handleCreateAsset"
      />
    </template>
  </MultiStepDialog>
</template>

<script setup>
import { ref, computed, inject, watch, onMounted } from "vue";
import { isEmpty } from "lodash";
import { storeToRefs } from "pinia";
import { useForm, usePage, router } from "@inertiajs/vue3";
import axios from "axios";
import route from "ziggy-js";
import { MultiStepDialog } from "@/SharedComponents/Dialogs";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useI18n } from "vue-i18n";
import { Header, Footer } from "@/Pages/Assets/Partials/Create";
import { Overview } from "@/Pages/Assets/Partials/Create/Steps";
import { useSites } from "@/Composables/useSites";
import {
  Date,
  Categories,
  Buildings,
  Spaces,
  AssetGroups,
} from "@/SharedComponents/Modals/Content";
import { useFilters } from "@/Composables/useFilters";
import { Steps } from "@/Composables/useSteps";
import { toast } from "@/SharedComponents/Toaster";

// Breakpoints
const { maxSmall } = useBreakpoints();

// Translations
const { t } = useI18n();

// Refs
const open = ref(false);
const createMore = ref(false);
const isLoading = ref(false);
const building = ref({});
const dialogRef = ref(null);
const componentRef = ref(null);

// Emitter
const emitter = inject("emitter");

// Filters: global
const globalFilterStore = useFilters("global");
const { filterValues: globalFilterValues } = storeToRefs(globalFilterStore);

// Buildings
const { isLoading: isLoadingBuildings, data: buildingsData } = useSites();

// Permissions
const { hasPermission, permissions } = inject("permissions");

// Form
const form = useForm({
  name: "",
  identifier: null,
  space: null,
  brand: null,
  supplier: null,
  group: null,
  serial_number: null,
  category: null,
  purchased_at: null,
  photo: null,
});

// Computed props
const buildingId = computed(
  () => building.value?.id || globalFilterValues.value.tenant?.[0] || ""
);

const buildings = computed(() => {
  if (isLoadingBuildings.value) {
    return [];
  }

  return buildingsData.value.filter(
    (building) =>
      hasPermission(permissions.createAsset, building.id) &&
      hasPermission(permissions.readAsset, building.id)
  );
});

const canCreateAssets = computed(() => {
  if (!buildingId.value) {
    return !isEmpty(buildings.value);
  }

  return hasPermission(permissions.createAsset, buildingId.value);
});

const steps = computed(
  () =>
    new Steps({
      overview: {
        content: Overview,
        props: {
          form: form,
          buildingId: buildingId.value,
          class: "border-secondary border-b",
        },
      },
      space: {
        title: t("components.asset_create.steps.space_select_title"),
        withSearch: true,
        content: Spaces,
        props: {
          initialState: form.space?.id,
          buildingId: buildingId.value,
          class: "-mx-2 pt-2 border-secondary bg-secondary border-t px-0",
        },
      },
      group: {
        title: t("components.asset_create.steps.group_select_title"),
        withSearch: true,
        content: AssetGroups,
        props: {
          initialState: form.group,
          buildingId: buildingId.value,
          class: "-mx-2 pt-2 border-secondary bg-secondary border-t px-0",
        },
      },
      buildings: {
        title: t("components.asset_create.steps.asset_destination"),
        withSearch: true,
        content: Buildings,
        props: {
          initialState: buildingId.value,
          buildings: buildings.value,
          class: "py-6 px-4 border-secondary bg-secondary border-t",
        },
      },
      purchased_at: {
        title: t("components.asset_create.steps.date_select_title"),
        content: Date,
        props: {
          initialState: form.purchased_at,
          class: "py-6 px-4 mt-5 border-secondary border-t",
        },
      },
      category: {
        title: t("components.asset_create.steps.category_select_title"),
        content: Categories,
        withSearch: true,
        props: {
          initialState: form.category,
          buildingIds: [buildingId.value],
          class: "py-6 px-4 border-secondary bg-secondary border-t",
        },
      },
    })
);

// Watchers
watch(building, () => {
  resetSiteSensitiveFields();
});

// Functions
const resetSiteSensitiveFields = () => {
  if (!form.isDirty) {
    return;
  }
  form.space = null;
  form.group = null;
  form.category = null;
};

const handleUpdateOpen = (value) => {
  if (dialogRef.value?.currentStep === "buildings" && buildingId.value) {
    dialogRef.value?.handleStepBack();
    return;
  }

  if (!buildingId.value && value) {
    dialogRef.value?.handleGoToStep("buildings");
  } else {
    dialogRef.value?.handleGoToStep("overview");
    building.value = null;
  }

  open.value = value;

  router.replace({
    url: !value ? route("assets.index") : route("assets.add"),
    preserveScroll: true,
    preserveState: true,
  });

  form.reset();
};

const handleUpdateCreateMore = (value) => {
  createMore.value = value;
};

const handleCreateAsset = () => {
  isLoading.value = true;

  let data = form;

  if (data.category?.id) {
    data.category_id = data.category.id;
  }

  if (data.space?.id) {
    data.space_id = data.space.id;
  }

  if (data.group?.id) {
    data.group_id = data.group.id;
  }

  const formDataPhoto = new FormData();
  formDataPhoto.append("photo", data.photo);

  axios
    .post(
      route("api.assets.add"),
      {
        ...data,
        tenant_id: buildingId.value,
      },
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    )
    .then(() => {
      toast.success(t("toasts.assets.add.success"));
      emitter.emit("reload-assets");
      handleUpdateOpen(false);
      router.visit(route("assets.index"), {
        preserveState: true,
      });
    })
    .catch((error) => {
      toast.error(t(error.response.data.message || "toasts.error"));
    })
    .finally(() => {
      isLoading.value = false;
      if (createMore.value) {
        handleUpdateOpen(true);
      }
    });
};

function handleSubmit(data, type = dialogRef.value?.currentStep) {
  if (type === "buildings") {
    building.value = data;
  } else {
    form[type] = data;
  }

  dialogRef.value?.handleGoToStep("overview");
}

const handleUpdateForm = (data) => {
  const { property, value } = data;
  form[property] = value;
};

onMounted(() => {
  if (usePage().url.includes("/admin/assets/new")) {
    handleUpdateOpen(true);
  }
});

watch(usePage(), () => {
  if (usePage().url.includes("/admin/assets/new")) {
    handleUpdateOpen(true);

    if (buildings.value.length === 1) {
      building.value = buildings.value[0];
      dialogRef.value?.handleGoToStep("overview");
    }
  }
});
</script>
